/**
 * Language choice for a visitor who has not picked a language in the switcher.
 *
 * `offered` must already be the dropdown order (l_app_language.display_order).
 *
 * 1. Facility / box `Lang`, when that code is in the dropdown.
 * 2. Browser language, when that exact code is in the dropdown.
 * 3. The first language in the dropdown.
 *
 * A saved switcher choice is applied first when `userSelected` is set and still offered.
 */

const PROVISIONAL_CODES = ['en', 'es', 'fr', 'ar'] as const;

export const LANG_STORAGE_KEY = 'lang';
export const LANG_COOKIE_KEY = 'pp_lang';
export const LANG_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

export function readUserSelectedLanguage(): string | null {
  try {
    const stored = localStorage.getItem(LANG_STORAGE_KEY)?.trim();
    if (stored) return stored;
  } catch { /* private mode */ }
  return readCookie(LANG_COOKIE_KEY);
}

export function writeUserSelectedLanguage(code: string): void {
  try {
    localStorage.setItem(LANG_STORAGE_KEY, code);
  } catch { /* private mode */ }
  writeCookie(LANG_COOKIE_KEY, code, LANG_COOKIE_MAX_AGE);
}

export function clearUserSelectedLanguage(): void {
  try {
    localStorage.removeItem(LANG_STORAGE_KEY);
  } catch { /* private mode */ }
  writeCookie(LANG_COOKIE_KEY, '', 0);
}

/** First paint, before the offered-language list has loaded. Prefix only, not the dropdown rules. */
export function detectBrowserLanguage(): 'en' | 'es' | 'fr' | 'ar' {
  for (const tag of browserLanguageTags()) {
    const primary = tag.split('-')[0]?.toLowerCase();
    const hit = PROVISIONAL_CODES.find(code => code === primary);
    if (hit) return hit;
  }
  return 'en';
}

export function findOfferedLanguage(offered: readonly string[], preferences: readonly string[]): string | null {
  return matchOfferedLanguage(offered, preferences);
}

export function browserLanguageTags(): string[] {
  if (typeof navigator === 'undefined') return [];
  const tags = navigator.languages?.length
    ? Array.from(navigator.languages)
    : [navigator.language];
  return tags.filter((tag): tag is string => !!tag?.trim());
}

/**
 * Pick the language to show when the visitor has not chosen one.
 * Returns null while `offered` is empty so the caller can keep the provisional value.
 */
export function pickLanguage(args: {
  userSelected: string | null;
  offered: readonly string[];
  configured: string | null | undefined;
  browserLanguages: readonly string[];
}): string | null {
  const offered = args.offered.map(code => code.trim()).filter(Boolean);
  if (offered.length === 0) return null;

  const selected = args.userSelected ? matchOfferedLanguage(offered, [args.userSelected]) : null;
  if (selected) return selected;

  const configured = args.configured?.trim()
    ? matchOfferedLanguage(offered, [args.configured.trim()])
    : null;
  if (configured) return configured;

  const browser = matchOfferedLanguage(offered, args.browserLanguages);
  if (browser) return browser;

  return offered[0]!;
}

/** True when a stored choice is still in the offered list. */
export function userSelectionIsOffered(userSelected: string | null, offered: readonly string[]): boolean {
  if (!userSelected) return false;
  return matchOfferedLanguage(offered, [userSelected]) !== null;
}

/** Case-insensitive equality only. `en` does not match `en-AU`. */
function matchOfferedLanguage(offered: readonly string[], preferences: readonly string[]): string | null {
  for (const preference of preferences) {
    const tag = preference.trim();
    if (!tag) continue;
    const exact = offered.find(code => code.toLowerCase() === tag.toLowerCase());
    if (exact) return exact;
  }
  return null;
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  for (const part of document.cookie.split(';')) {
    const trimmed = part.trim();
    const eq = trimmed.indexOf('=');
    if (eq === -1) continue;
    if (trimmed.slice(0, eq) === name) {
      const value = decodeURIComponent(trimmed.slice(eq + 1)).trim();
      return value || null;
    }
  }
  return null;
}

function writeCookie(name: string, value: string, maxAge: number): void {
  if (typeof document === 'undefined') return;
  document.cookie = `${name}=${encodeURIComponent(value)}; max-age=${maxAge}; path=/; SameSite=Lax`;
}
